import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dayjs } from 'dayjs';
import { IWeekView } from 'components/Calendar/DayPicker';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { getPayPeriodsBetweenRequest, getPayPeriodsRequest, PayPeriods, selectPayPeriods } from 'store/Collections/PayPeriods';
import { IStore } from 'store/types';
import { IPayPeriod } from 'types';
import { formatDateForApi } from 'utils/date';
import { findPayPeriod } from 'utils/payPeriods';

/**
 * A site's pay periods, newest first, loaded a page at a time and shared by every screen with the period picker.
 * `from`, the start of the selected period, loads every period from it to today instead of the first page.
 */
export const usePayPeriods = (orgId: string, siteId: string, from?: string) => {
  const dispatch = useDispatch();
  const { data: payPeriods } = useSelector(selectPayPeriods({ orgId, siteId })) || {};
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, PayPeriods.GET_PAY_PERIODS_REQUEST));

  useEffect(() => {
    if (orgId && siteId && !payPeriods) dispatch(getPayPeriodsRequest({ orgId, siteId, from }));
  }, [orgId, siteId, from, payPeriods, dispatch]);

  return {
    payPeriods: payPeriods || [],
    isLoadingFirstPage: !payPeriods,
    isLoadingMore: isLoading && !!payPeriods,
    loadMore: (before: string) => dispatch(getPayPeriodsRequest({ orgId, siteId, before })),
  };
};

/**
 * The pay period of the day picked on a date step's calendar, loaded fresh, not cached, with each page of visible days.
 */
export const useDayPayPeriod = (siteId: string, visibleDays: IWeekView[], selectedDay?: Dayjs) => {
  const dispatch = useDispatch();
  const start = visibleDays.length ? formatDateForApi(visibleDays[0].weekday) : undefined;
  const end = visibleDays.length ? formatDateForApi(visibleDays[visibleDays.length - 1].weekday) : undefined;
  const [payPeriods, setPayPeriods] = useState<IPayPeriod[]>([]);
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, PayPeriods.GET_PAY_PERIODS_BETWEEN_REQUEST));

  useEffect(() => {
    if (!siteId || !start || !end) return;
    setPayPeriods([]);
    dispatch(getPayPeriodsBetweenRequest({ siteId, start, end, onSuccess: setPayPeriods }));
  }, [siteId, start, end, dispatch]);

  return { selectedPayPeriod: findPayPeriod(payPeriods, selectedDay && formatDateForApi(selectedDay)), isLoadingPayPeriods: isLoading };
};
